import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, TextField, Button, MenuItem, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { es } from "date-fns/locale";
import { addCharge, getConcepts, updateCharge, getFutureCharges } from "../services/firestore";

// PCS Admin React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

const ChargeForm = ({ proyectoId, initialValues = {}, onSaved }) => {
  const [concepts, setConcepts] = useState([]);
  const [formData, setFormData] = useState({
    concepto_id: initialValues?.concepto_id || "",
    monto: initialValues?.monto || "",
    fecha_vencimiento: initialValues?.fecha_vencimiento ? new Date(initialValues.fecha_vencimiento) : null,
    estado: initialValues?.estado || "Pendiente",
  });
  const [errors, setErrors] = useState({});
  const [fechasFuturas, setFechasFuturas] = useState([]);
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [isEditing, setIsEditing] = useState(!!initialValues?.id);

  // Mostrar estado inicial
  console.log("Estado Inicial:", {
    proyectoId,
    concepts,
    formData,
    errors,
    fechasFuturas,
    selectedConcept,
    isEditing
  });

  useEffect(() => {
    const loadConcepts = async () => {
      try {
        const conceptsData = await getConcepts();
        console.log("Conceptos cargados desde Firestore:", conceptsData);
        setConcepts(conceptsData);

        // Si estamos editando, cargar el concepto seleccionado
        if (initialValues?.concepto_id) {
          const concept = conceptsData.find(c => c.id === initialValues.concepto_id);
          if (concept) {
            console.log("Concepto encontrado para edición:", concept);
            setSelectedConcept(concept);
          }
        }
      } catch (error) {
        console.error("Error al cargar conceptos:", error);
      }
    };
    loadConcepts();
  }, [initialValues?.concepto_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log("Cambio en formulario:", { name, value });

    setFormData((prev) => {
      const newFormData = {
        ...prev,
        [name]: value,
      };
      console.log("Nuevo estado del formulario:", newFormData);
      return newFormData;
    });

    // Si se cambia el concepto o la fecha de vencimiento, actualizar las fechas futuras
    if (name === "concepto_id" || name === "fecha_vencimiento") {
      const concept = concepts.find(
        (c) => c.id === (name === "concepto_id" ? value : formData.concepto_id)
      );
      console.log("Concepto encontrado:", concept);
      setSelectedConcept(concept);

      const fechaBase = name === "fecha_vencimiento" ? value : formData.fecha_vencimiento;
      console.log("Fecha base para cálculos:", fechaBase);

      if (concept && fechaBase) {
        const fechas = [];
        if (concept.periodicidad === "mensual") {
          // Generar fechas para los próximos 11 meses (12 meses en total)
          const fechaInicial = new Date(fechaBase);
          console.log("Fecha inicial para cálculos mensuales:", fechaInicial);
          
          for (let i = 1; i <= 11; i++) {
            const nextDate = new Date(fechaInicial);
            nextDate.setMonth(nextDate.getMonth() + i);
            const fechaFormateada = nextDate.toISOString().split("T")[0];
            console.log(`Generando fecha mensual ${i}:`, fechaFormateada);
            fechas.push(fechaFormateada);
          }
          console.log("Total de fechas mensuales generadas:", fechas.length + 1); // +1 por la fecha base
        } else if (concept.periodicidad === "anual") {
          // Generar fechas para los próximos 4 años (5 años en total)
          const fechaInicial = new Date(fechaBase);
          console.log("Fecha inicial para cálculos anuales:", fechaInicial);
          
          for (let i = 1; i <= 4; i++) {
            const nextDate = new Date(fechaInicial);
            nextDate.setFullYear(nextDate.getFullYear() + i);
            const fechaFormateada = nextDate.toISOString().split("T")[0];
            console.log(`Generando fecha anual ${i}:`, fechaFormateada);
            fechas.push(fechaFormateada);
          }
          console.log("Total de fechas anuales generadas:", fechas.length + 1); // +1 por la fecha base
        }
        console.log("Fechas futuras generadas:", fechas);
        setFechasFuturas(fechas);
      } else {
        setFechasFuturas([]);
      }
    }

    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = {
          ...prev,
          [name]: "",
        };
        console.log("Errores actualizados:", newErrors);
        return newErrors;
      });
    }
  };

  const handleDateChange = (date) => {
    console.log("Fecha seleccionada:", date);
    setFormData((prev) => ({
      ...prev,
      fecha_vencimiento: date,
    }));

    // Generar fechas futuras cuando se selecciona la fecha
    if (selectedConcept && (selectedConcept.periodicidad === "mensual" || selectedConcept.periodicidad === "anual")) {
      const fechas = [];
      const fechaInicial = new Date(date);
      console.log("Generando fechas futuras desde:", fechaInicial);

      if (selectedConcept.periodicidad === "mensual") {
        for (let i = 1; i <= 11; i++) {
          const nextDate = new Date(fechaInicial);
          nextDate.setMonth(nextDate.getMonth() + i);
          const fechaFormateada = nextDate.toISOString().split("T")[0];
          console.log(`Generando fecha mensual ${i}:`, fechaFormateada);
          fechas.push(fechaFormateada);
        }
        console.log("Total de fechas mensuales generadas:", fechas.length + 1);
      } else if (selectedConcept.periodicidad === "anual") {
        for (let i = 1; i <= 4; i++) {
          const nextDate = new Date(fechaInicial);
          nextDate.setFullYear(nextDate.getFullYear() + i);
          const fechaFormateada = nextDate.toISOString().split("T")[0];
          console.log(`Generando fecha anual ${i}:`, fechaFormateada);
          fechas.push(fechaFormateada);
        }
        console.log("Total de fechas anuales generadas:", fechas.length + 1);
      }

      console.log("Fechas futuras generadas:", fechas);
      setFechasFuturas(fechas);
    }

    if (errors.fecha_vencimiento) {
      setErrors((prev) => ({
        ...prev,
        fecha_vencimiento: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Iniciando envío del formulario");
    const newErrors = {};

    if (!formData.concepto_id) {
      newErrors.concepto_id = "El concepto es requerido";
    }
    if (!formData.monto) {
      newErrors.monto = "El monto es requerido";
    }
    if (!formData.fecha_vencimiento) {
      newErrors.fecha_vencimiento = "La fecha es requerida";
    }

    console.log("Validación de errores:", newErrors);

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const chargeData = {
        ...formData,
        proyecto_id: proyectoId,
        monto: parseFloat(formData.monto),
        fecha_vencimiento: new Date(formData.fecha_vencimiento).toISOString(),
      };

      console.log("Datos del cobro a guardar:", chargeData);

      if (isEditing) {
        // Si estamos editando, actualizar el cobro actual
        await updateCharge(initialValues.id, chargeData);
        console.log("Cobro actualizado");

        // Si el concepto es recurrente, actualizar los cobros futuros
        if (selectedConcept && 
            (selectedConcept.periodicidad === "mensual" || selectedConcept.periodicidad === "anual")) {
          console.log("Actualizando cobros futuros para concepto recurrente:", selectedConcept.periodicidad);
          
          // Obtener todos los cobros futuros del mismo concepto
          const fechaBase = new Date(formData.fecha_vencimiento);
          fechaBase.setDate(fechaBase.getDate() + 1); // Excluir el cobro actual
          
          console.log("Buscando cobros futuros desde:", fechaBase);
          const futureCharges = await getFutureCharges(
            proyectoId, 
            selectedConcept.id, 
            fechaBase
          );
          console.log("Cobros futuros encontrados:", futureCharges);

          // Calcular el intervalo entre fechas
          const fechaOriginal = new Date(initialValues.fecha_vencimiento);
          const fechaNueva = new Date(formData.fecha_vencimiento);
          const diferenciaDias = Math.round((fechaNueva - fechaOriginal) / (1000 * 60 * 60 * 24));
          console.log("Diferencia en días:", diferenciaDias);

          // Actualizar cada cobro futuro
          for (const charge of futureCharges) {
            const fechaActual = new Date(charge.fecha_vencimiento);
            fechaActual.setDate(fechaActual.getDate() + diferenciaDias);
            
            const updatedCharge = {
              ...chargeData,
              id: charge.id,
              fecha_vencimiento: fechaActual.toISOString()
            };
            console.log("Actualizando cobro futuro:", updatedCharge);
            await updateCharge(charge.id, updatedCharge);
          }
          console.log("Todos los cobros futuros actualizados");
        }
      } else {
        // Crear el cobro actual
        await addCharge(chargeData);
        console.log("Cobro actual guardado");

        // Crear los cobros futuros si el concepto es recurrente
        if (selectedConcept && 
            (selectedConcept.periodicidad === "mensual" || selectedConcept.periodicidad === "anual")) {
          console.log("Creando cobros futuros para concepto recurrente:", selectedConcept.periodicidad);
          
          for (const fecha of fechasFuturas) {
            const futureCharge = {
              ...chargeData,
              fecha_vencimiento: new Date(fecha).toISOString(),
            };
            console.log("Guardando cobro futuro para fecha:", fecha);
            await addCharge(futureCharge);
          }
          console.log("Todos los cobros futuros guardados exitosamente");
        }
      }

      console.log("Proceso de guardado completado");
      onSaved();
    } catch (error) {
      console.error("Error al guardar cobro:", error);
      setErrors({ submit: "Error al guardar el cobro" });
    }
  };

  const getFechaLabel = () => {
    if (!selectedConcept) return "Fecha de Vencimiento";
    return selectedConcept.periodicidad === "ninguna" ? "Fecha de Vencimiento" : "Fecha de Cobro";
  };

  const getPeriodicidadLabel = () => {
    if (!selectedConcept) return "";
    const periodicidades = {
      ninguna: "Sin periodicidad",
      mensual: "Mensual",
      anual: "Anual",
    };
    return periodicidades[selectedConcept.periodicidad];
  };

  return (
    <MDBox component="form" onSubmit={handleSubmit}>
      <MDInput
        select
        fullWidth
        label="Concepto"
        name="concepto_id"
        value={formData.concepto_id}
        onChange={handleChange}
        error={!!errors.concepto_id}
        helperText={errors.concepto_id}
        required
        sx={{ mb: 2 }}
      >
        {concepts.map((concept) => (
          <MenuItem key={concept.id} value={concept.id}>
            {concept.nombre}
          </MenuItem>
        ))}
      </MDInput>
      {selectedConcept && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
            Periodicidad: {getPeriodicidadLabel()}
          </Typography>
          {isEditing && (
            <Typography variant="caption" color="info" sx={{ mb: 2, display: "block" }}>
              Nota: Al editar este cobro, se actualizarán también todos los cobros futuros del mismo concepto.
            </Typography>
          )}
        </>
      )}
      <MDInput
        fullWidth
        label="Monto"
        name="monto"
        type="number"
        value={formData.monto}
        onChange={handleChange}
        error={!!errors.monto}
        helperText={errors.monto}
        required
        sx={{ mb: 2 }}
      />
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
        <DatePicker
          label={getFechaLabel()}
          value={formData.fecha_vencimiento}
          onChange={handleDateChange}
          renderInput={(params) => (
            <MDInput
              {...params}
              fullWidth
              error={!!errors.fecha_vencimiento}
              helperText={errors.fecha_vencimiento}
              required
              sx={{ mb: 2 }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          )}
        />
      </LocalizationProvider>
      {fechasFuturas.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {isEditing ? "Se actualizarán los siguientes cobros:" : "Se crearán cobros adicionales para las siguientes fechas:"}
          </Typography>
          <Box sx={{ 
            maxHeight: '200px', 
            overflowY: 'auto', 
            p: 1, 
            bgcolor: 'background.paper',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider'
          }}>
            {fechasFuturas.map((fecha, index) => (
              <Typography 
                key={index} 
                variant="body2" 
                color="text.secondary"
                sx={{ 
                  py: 0.5,
                  borderBottom: index < fechasFuturas.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider'
                }}
              >
                {new Date(fecha).toLocaleDateString('es-ES', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
      {errors.submit && <MDBox sx={{ color: "error.main", mb: 2 }}>{errors.submit}</MDBox>}
      <MDButton variant="gradient" color="success" type="submit" fullWidth>
        {isEditing ? "Actualizar" : "Guardar"}
      </MDButton>
    </MDBox>
  );
};

ChargeForm.propTypes = {
  proyectoId: PropTypes.string.isRequired,
  initialValues: PropTypes.shape({
    id: PropTypes.string,
    concepto_id: PropTypes.string,
    monto: PropTypes.number,
    fecha_vencimiento: PropTypes.string,
  }),
  onSaved: PropTypes.func,
};

export default ChargeForm; 